import React, { useState } from 'react';
import { Copy, Check, Hash, Tag, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';
import { copyToClipboardFallback } from '../lib/utils';

const MainThemeBox = ({ theme }) => (
  <div className="md:col-span-12 border border-border bg-card p-6">
    <div className="flex items-center gap-2 mb-3 text-muted-foreground">
      <FileText size={18} />
      <h3 className="text-sm font-bold uppercase tracking-widest">Main Theme</h3>
    </div>
    <p className="text-lg md:text-xl font-medium leading-relaxed">
      {theme}
    </p>
  </div>
);

const TagBox = ({ title, icon: Icon, tags, onExport, onCopy, copiedTags }) => (
  <div className="md:col-span-8 border border-border bg-card p-6 relative group">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon size={18} />
        <h3 className="text-sm font-bold uppercase tracking-widest">{title}</h3>
      </div>
      {onExport && onCopy && (
        <div className="flex items-center gap-2">
          <button
            onClick={onExport}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase tracking-widest border border-border bg-secondary text-secondary-foreground hover:bg-muted transition-colors"
            data-testid="export-csv-btn"
            title="Export to CSV"
          >
            <Download size={14} />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={onCopy}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase tracking-widest bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
            data-testid="copy-tags-btn"
          >
            {copiedTags ? <Check size={14} /> : <Copy size={14} />}
            {copiedTags ? 'Copied' : 'Copy All'}
          </button>
        </div>
      )}
    </div>
    <div className="flex flex-wrap gap-2">
      {tags.map((tag) => (
        <span 
          key={tag}
          className="px-3 py-1 bg-secondary text-secondary-foreground text-sm font-mono hover:bg-accent hover:text-accent-foreground transition-colors cursor-default"
        >
          {tag}
        </span>
      ))}
    </div>
  </div>
);

const HashtagBox = ({ tags }) => (
  <div className="md:col-span-4 border border-border bg-card p-6">
    <div className="flex items-center gap-2 mb-4 text-muted-foreground">
      <Hash size={18} />
      <h3 className="text-sm font-bold uppercase tracking-widest">Hashtags</h3>
    </div>
    <div className="flex flex-col gap-2">
      {tags.map((tag) => (
        <span 
          key={tag}
          className="text-sm font-medium hover:text-accent transition-colors cursor-pointer"
        >
          {tag}
        </span>
      ))}
    </div>
  </div>
);

const ResultSection = ({ result }) => {
  const [copiedTags, setCopiedTags] = useState(false);

  const handleCopyTags = async () => {
    const tagsString = result.tags.join(', ');
    const success = await copyToClipboardFallback(tagsString);
    if (success) {
        setCopiedTags(true);
        toast.success('Tags copied to clipboard');
        setTimeout(() => setCopiedTags(false), 2000);
    } else {
        toast.error('Failed to copy tags to clipboard');
    }
  };

  const handleExportCSV = () => {
    try {
      let csvContent = "data:text/csv;charset=utf-8,";
      csvContent += "Keyword,Type\n";
      
      result.tags.forEach(tag => {
        csvContent += `"${tag.replace(/"/g, '""')}",Tag\n`;
      });
      
      result.hashtags.forEach(tag => {
        csvContent += `"${tag.replace(/"/g, '""')}",Hashtag\n`;
      });

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `youtube_tags_${new Date().getTime()}.csv`);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Tags exported to CSV');
    } catch (error) {
      toast.error('Failed to export tags to CSV');
    }
  };

  if (!result) return null;

  return (
    <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <MainThemeBox theme={result.main_theme} />
      <TagBox 
        title="Suggested Tags" 
        icon={Tag} 
        tags={result.tags} 
        onExport={handleExportCSV} 
        onCopy={handleCopyTags} 
        copiedTags={copiedTags} 
      />
      <HashtagBox tags={result.hashtags} />
    </div>
  );
};

export default ResultSection;